<template>
  <div class="panel panel-default relative" :class="{'has-error': hasErrors}">
    <div class="panel-heading">
      <h2 class="panel-title">
        <b-button v-b-toggle="documentKey" @click.once="requestFiles" class="btn btn-link btn-collapse"
                  style="width: auto; padding: 2px; text-align: left; overflow: hidden;"
        >
          {{ documentName }}
        </b-button>
      </h2>
    </div>
    <b-collapse :id="documentKey" class="panel-collapse relative">
      <div class="panel-body relative">
        <div class='row'>
          <div v-if='isDocumentNeedNumber' class="col-md-4">
            <document-number-input
              v-model="number"
              :errors="hasErrors || {}"
              @blur="emitNumberChanged"
            />
          </div>
          <div class="col-md-4">
            <document-validity-period-input
              v-if="isDocumentNeedExpire"
              v-model="date_of_expire"
              :check-foreign-visitors="this.checkForeignVisitors"
              :date-format="this.dateFormat"
              :date-mask="this.dateMask"
              :date-value-type="this.dateValueType"
              :disabled-dates="this.disabledDates"
              :disabled="disabledDateOfExpire"
              :document-id="this.documentId"
              :document="this.documents[0]"
              :entity-id="this.entityId"
              :entity-type="this.entityType"
              :form="this.form"
              :max-date="this.maxDate"
              :min-date="this.minDate"
              :timezone="this.timezone"
              @updateParentChecked="updateChecked"
            />
          </div>
        </div>
        <dashboard
          v-if="dashboardShowCondition"
          :id="`dashboard-${documentKey}`"
          :key="`dashboard${$vnode.key}`"
          :props="dashboardOptions"
          :uppy="uppy"
        ></dashboard>
        <div v-if="documentsShowCondition">
          <b-modal
            :class="'documents-modal'"
            size="lg"
            :id="modalId"
          >
            <document-modal :documents="documents" :selected="applicationDocumentPreviewIndex">
              <slot name="left-side-modal"/>
            </document-modal>
            <template v-slot:modal-title>
              &nbsp;
            </template>
            <template v-slot:modal-footer>
              <button type="button" class="btn btn-default" @click="$bvModal.hide(modalId)" aria-label="Close">Закрыть</button>
            </template>
          </b-modal>
          <div class="images-wrapper" style="display: inline-flex; flex-direction: row; flex-wrap: wrap; width: 100%">
            <div style="width: 100%; text-align: center; color: #d54444"
                 v-if="documents.length === 0 && !loadingDocuments">Файлы отсутствуют
            </div>
            <div v-for="(doc, key) in documents || []" class="image"
                 :key="doc.key"
                 :style="{ backgroundImage: 'url(' + doc.thumb + ')' }"
                 style="width: 100px; height: 100px; display: inline; margin: 5px; background-repeat: no-repeat; background-size: 100px; background-position: center"
                 @click="applicationDocumentPreviewIndex = key; $bvModal.show(modalId)"
            >
            </div>
          </div>
        </div>
      </div>
      <div v-if="loadingDocuments" class="loader"></div>
    </b-collapse>
    <div class="document-circles-menu">
      <hint :value="documentComment" style="right: 5px; top: 6px;"/>
      <div v-if="canShowDateOfExpireStatus">
        <div
          :class="{'date_of_expire_accept_status_yellow': !showDateOfExpireAccepted, 'date_of_expire_accept_status_green': showDateOfExpireAccepted}"
          style="right: 5px; top: 6px">
          <label>{{ showDateOfExpireAccepted ? '✓' : '!' }}</label>
          <div class="form-fieldhelp-text">
            <div>
              {{ showDateOfExpireAccepted ? 'Дата окончания действия документа подтверждена' : 'Дата окончания действия документа не&nbsp;подтверждена'
              }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import applicationFormContextMixin from '@/common/mixins/applicationFormContextMixin'
import applicationFormMixin from '@/common/mixins/applicationFormMixin'
import { BaseApiClient } from '@/common/helpers/BaseApiClient'
import dashboard from '@uppy/vue/src/dashboard'
import datePickerConfigMixin from '@/common/mixins/application-form/datePickerConfigMixin'
import DocumentValidityPeriodInput from '@/common/components/application/form-inputs/visitors/DocumentValidityPeriodInput.vue'
import RusUppy from '@uppy/locales/lib/ru_RU'
import Uppy from '@uppy/core'
import UppyUrlDrop from '@/common/lib/UppyUrlDrop'
import { UserRoles } from '@/common/enums/UserRoles'
import { v4 as uuidv4 } from 'uuid'
import XHRUpload from '@uppy/xhr-upload'
import moment from 'moment'
import FileMeta from '@/common/classes/VisitorDocumentList/FileMeta'
import VisitorDocumentListItem from '@/common/classes/VisitorDocumentList/VisitorDocumentListItem'
import UppyFileMeta from '@/common/classes/VisitorDocumentList/UppyFileMeta'
import Hint from '@/common/components/inputs/components/Hint.vue'
import DocumentNumberInput from '../application/form-inputs/visitors/DocumentNumberInput.vue'
import DocumentModal from '@/common/components/DocumentModal.vue';
import mainMixin from '@/common/mixins/mainMixin';

export default {
  name: 'UppyUploader',

  components: {
    DocumentModal,
    Hint,
    DocumentValidityPeriodInput,
    DocumentNumberInput,
    dashboard
  },

  mixins: [
    applicationFormContextMixin,
    applicationFormMixin,
    datePickerConfigMixin,
    mainMixin
  ],

  model: {
    prop: 'documents',
    event: 'change'
  },

  props: {
    mimeTypes: {
      type: Array,
      default: () => []
    },
    dashboardOptions: {
      type: Object,
      default: () => ({
        browserBackButtonClose: false,
        doneButtonHandler: null,
        height: 318,
        inline: true,
        proudlyDisplayPoweredByUppy: false,
        showLinkToFileUploadResult: true,
        showProgressDetails: true,
        showRemoveButtonAfterComplete: true,
        width: '100%'
      })
    },

    form: {
      type: Object,
      default: () => ({})
    },

    dashboardShowCondition: {
      type: Boolean,
      required: true
    },

    documentComment: {
      type: String
    },

    documentId: {
      type: Number,
      required: true
    },

    documentKey: {
      type: String,
      required: true
    },

    documentName: {
      type: String
    },

    documentNeedExpire: {
      required: true
    },

    documentNeedNumber: {
      type: Number,
      required: false
    },

    documentShortName: {
      type: String
    },

    maxFilesCount: {
      type: Number
    },

    documents: {
      type: Array,
      default: () => [],
      required: true
    },

    documentsShowCondition: {
      type: Boolean,
      required: true
    },

    entityId: {
      required: true
    },

    entityType: {
      type: String,
      required: true
    },

    filesRequest: {
      type: Function,
      default: () => {
      }
    },

    getUuid: {
      type: Function,
      required: true
    },

    hasErrors: {
      type: [Boolean, Object]
    },

    ownerType: {
      type: String
    }
  },

  data() {
    const baseUrl = process.env.VUE_APP_API_URL
    let allowedFileTypes = this.mimeTypes
    RusUppy.strings.done = 'Назад'
    if (allowedFileTypes.includes('application/pkcs7-signature')) {
      allowedFileTypes = ['*/*']
    }

    const uppy = new Uppy({
      id: uuidv4(),
      locale: RusUppy,
      allowMultipleUploads: true,
      autoProceed: true,
      debug: false,
      restrictions: {
        maxFileSize: 1024 * 1024 * (this.configuration?.max_file_size_mb ?? 2),
        minFileSize: null,
        maxTotalFileSize: null,
        maxNumberOfFiles: this.maxFilesCount,
        minNumberOfFiles: 1,
        allowedFileTypes
      }
    }).use(XHRUpload, {
      limit: 10,
      headers: BaseApiClient.getAuthHeaders(),
      endpoint: baseUrl + '/upload/docs',
      formData: true,
      fieldName: 'file',
      metaFields: ['uuid', 'document_type', 'type']
    }).use(UppyUrlDrop)

    uppy.on('file-added', (file) => {
      const uuid = file.meta.uuid ?? uuidv4()
      uppy.setFileMeta(file.id, new UppyFileMeta({
        date_of_expire: moment(this.date_of_expire, this.dateFormat),
        number: this.number,
        document_type: this.documentShortName,
        filename: file.name,
        type: this.ownerType,
        uuid
      }))
      this.$emit('file-added', file)
    })

    uppy.on('file-removed', (file) => {
      this.deleteDocument(file.meta.uuid)
      this.$emit('file-removed', file)
    })

    uppy.on('upload-success', file => {
      this.addDocument(
        new VisitorDocumentListItem({
          title: file.name,
          mediaType: 'download',
          mimeType: file.type,
          meta: new FileMeta({
            checked: false,
            date_of_expire: moment(file.meta.date_of_expire ?? this.date_of_expire, this.dateFormat),
            number: file.meta.number ?? this.number,
            filename: file.name,
            uuid_any: file.meta.uuid
          }),
          src: URL.createObjectURL(file.data),
          thumb: URL.createObjectURL(file.data)
        })
      )

      this.$emit('upload-success', file)
    })

    uppy.on('upload-error', (file, error, response) => {
      uppy.hideInfo()
      uppy.info(response.body.message, 'error', 5000)
    })

    return {
      applicationDocumentPreviewIndex: null,
      documentRequestsQueue: new Promise(resolve => resolve()),
      loadingDocuments: false,
      date_of_expire: null,
      number: null,
      checked: this?.documents[0]?.meta.checked,
      uppy,
      customPreviewDoc: null
    }
  },

  computed: {
    modalId() {
      return `${this.entityType}-document-modal-${this.documentKey}`
    },
    canShowDateOfExpireStatus() {
      return this.isView && this.isDocumentNeedExpire
    },

    disabledDateOfExpire() {
      if (this.userIsSB) {
        return this.showDateOfExpireAccepted
      }

      return this.disabledInput
    },

    isDocumentNeedExpire() {
      return this.documentNeedExpire
    },

    isDocumentNeedNumber() {
      return this.documentNeedNumber != null
    },

    showDateOfExpireAccepted() {
      return !!Number(this.checked)
    },

    userIsSB() {
      return this.user.role === UserRoles.SB
    }
  },

  watch: {
    documents(value) {
      this.addLoadDocumentRequest(this.updateUppyDocuments)
    },

    date_of_expire(value) {
      this.emitUpdatedDocuments({ date_of_expire: moment(value, this.dateFormat) })
    },

    number(value) {
      this.emitUpdatedDocuments({ number: value })
    }
  },

  mounted() {
    this.setMetaFields()
  },

  methods: {
    /**
     * @param {VisitorDocumentListItem} document
     */
    async addFile(document) {
      const meta = new UppyFileMeta({
        date_of_expire: moment(this.date_of_expire, this.dateFormat),
        number: this.number,
        document_type: this.documentShortName,
        filename: document.meta?.filename,
        type: this.ownerType,
        uuid: document.meta?.uuid_any
      })
      const mimeTypes = this.mimeTypes
      const progress = {
        uploadStarted: true,
        uploadComplete: true,
        percentage: 100
      };
      const uppy = this.uppy
      /**
       * @param {AddFileOptions} mixin
       */
      const addFailureFile = function (mixin) {
        const fileId = uppy.addFile(
          Object.assign(
            {
              name: meta.filename,
              type: mimeTypes[0] ?? 'application/octet-stream',
              meta
            },
            mixin
          )
        )
        // https://github.com/transloadit/uppy/issues/1112#issuecomment-432339569
        uppy.setFileState(fileId, { progress })

        return fileId
      }

      if (!document.src || !meta.filename || !meta.uuid) {
        const uuid = uuidv4()
        return addFailureFile({
          data: this.$store.getters.errorImages.invalidObject,
          preview: URL.createObjectURL(this.$store.getters.errorImages.invalidObject),
          name: uuid,
          meta: new UppyFileMeta({
            date_of_expire: moment(this.date_of_expire, this.dateFormat),
            number: this.number,
            document_type: this.documentShortName,
            filename: uuid,
            type: this.ownerType,
            uuid: uuid
          })
        })
      }

      let response
      try {
        response = await fetch(document.src)
      } catch (e) {
        return addFailureFile({
          data: this.$store.getters.errorImages.fetchGeneralFail,
          preview: URL.createObjectURL(this.$store.getters.errorImages.fetchGeneralFail)
        })
      }

      if (this.$store.getters.errorImages[response.status]) {
        return addFailureFile({
          data: this.$store.getters.errorImages[response.status],
          preview: URL.createObjectURL(this.$store.getters.errorImages[response.status])
        })
      }

      try {
        const blob = await response.blob()
        const fileId = uppy.addFile({
          data: blob,
          name: document.meta.filename,
          type: blob.type,
          meta
        })
        // https://github.com/transloadit/uppy/issues/1112#issuecomment-432339569
        uppy.setFileState(fileId, { progress })

        return fileId
      } catch (e) {
        if (e.isResriction) {
          return addFailureFile({
            data: this.$store.getters.errorImages.imageIsRestricted,
            preview: URL.createObjectURL(this.$store.getters.errorImages.imageIsRestricted)
          })
        } else {
          return addFailureFile({
            data: this.$store.getters.errorImages.uppyError,
            preview: URL.createObjectURL(this.$store.getters.errorImages.uppyError)
          })
        }
      }
    },

    /**
     * Exists to prevent theoretical Race Condition
     * @param {Function} onfulfilled Cant throws
     */
    addLoadDocumentRequest(onfulfilled) {
      this.documentRequestsQueue = this.documentRequestsQueue
        .then(() => {
          this.loadingDocuments = true
        })
        // eslint-disable-next-line no-console
        .then(onfulfilled, console.error)
        .then(() => {
          this.loadingDocuments = false
        })
    },

    onCoolLightBoxOpen(index) {
      const file = this.documents[index]
      if (file?.mediaType === 'download') {
        this.$refs.coolLightBox.close()
        const a = document.createElement('a')
        a.href = file.src
        a.download = file.title
        a.click()
      }
    },

    addDocument(file) {
      this.$emit('change', [...this.documents, file])
    },

    deleteDocument(uuid) {
      this.$emit('change', this.documents.filter(file => file.meta.uuid_any !== uuid))
    },

    updateUppyDocuments() {
      const existingFiles = this.uppy.getFiles().map(file => file.meta.uuid)

      return Promise.allSettled(
        this.documents
          .filter(document => !existingFiles.includes(document?.meta?.uuid_any))
          .map(this.addFile)
      )
    },

    requestFiles() {
      this.addLoadDocumentRequest(this.updateUppyDocuments)
      this.addLoadDocumentRequest(this.filesRequest)
      this.addLoadDocumentRequest(this.setMetaFields)
    },

    setMetaFields() {
      let date_of_expire = this.documents?.[0]?.meta?.date_of_expire ?? this.date_of_expire ?? null
      if (moment.isMoment(date_of_expire) && date_of_expire.isValid()) {
        date_of_expire = date_of_expire.format(this.dateFormat)
      }
      if (typeof date_of_expire === typeof 'String') {
        this.date_of_expire = date_of_expire
      }

      this.number = this.documents?.[0]?.meta?.number ?? this.number ?? null
    },

    updateChecked(value) {
      this.checked = value
    },

    emitUpdatedDocuments(updatedFields = {}) {
      const documents = this.documents.map(file =>
        new VisitorDocumentListItem({
          meta: new FileMeta({
            checked: file.meta.checked,
            date_of_expire: updatedFields.date_of_expire ?? moment(this.date_of_expire, this.dateFormat),
            number: updatedFields.number ?? this.number,
            filename: file.meta.filename,
            uuid_any: file.meta.uuid_any
          }),
          mediaType: file.mediaType,
          mimeType: file.mimeType,
          src: file.src,
          thumb: file.thumb,
          title: file.title
        })
      )

      this.$emit('change', documents)
    },

    emitNumberChanged(value) {
      const documentShortName = this.documentShortName
      this.$emit('number-changed', { number: value, documentShortName })
    }
  }
}
</script>

<style>
.modal-dialog.modal-lg {
  min-height: 80%;
  min-width: 80%;
}
.swiper-slide {
  height: auto;
}
.swiper-wrapper {
  min-height: 50vh;
}
.modal-dialog.modal-lg .modal-content .modal-header {
  display: none;
}

</style>
